import { paths } from 'config/path_helpers';
import fetch from 'utilities/fetch';

import { genericErrorMessage, PaymentError } from './errors';
import {
  CreatePurchaseAgreementParams,
  CreatePurchaseAgreementResponse,
  PaymentMethod
} from './types';

export const submitPurchaseAgreement = async (params: CreatePurchaseAgreementParams): Promise<CreatePurchaseAgreementResponse> => {
  return await fetch(
    paths.api.purchaseAgreements(),
    {
      method: 'POST',
      body: JSON.stringify(params)
    }
  );
};

export const createPurchaseAgreement = async (stripe, params: CreatePurchaseAgreementParams): Promise<CreatePurchaseAgreementResponse> => {
  const response = await submitPurchaseAgreement(params);

  if (!response.actionRequired && response.nextUrl) {
    return response;
  } else if (response.actionRequired?.type === 'offsiteConfirmation') {
    return response;
  } else if (response.actionRequired?.type === 'confirmPayment') {
    return await confirmStripePayment(
      stripe,
      response.actionRequired.clientSecret,
      response.actionRequired.paymentMethodId,
      response.nextUrl,
      params.paymentMethod === PaymentMethod.ALIPAY ? 'alipay' : 'card'
    );
  } else {
    throw new PaymentError(
      genericErrorMessage,
      `Unexpected response in createPurchaseAgreement with params ${JSON.stringify(params)} and response ${JSON.stringify(response)}`
    );
  }
};

export const confirmStripePayment = async (stripe, clientSecret, paymentMethodId = null, nextUrl = null, paymentMethodType = null) => {
  const isAlipay = paymentMethodType === 'alipay'

  const options: any = {};
  if (paymentMethodId) {
    options.payment_method = paymentMethodId;
    if (!isAlipay) {
      options.setup_future_usage = 'off_session';
    }
  }
  if (nextUrl) {
    options.return_url = nextUrl
  }

  const response = isAlipay ?
    await stripe.confirmAlipayPayment(clientSecret, options)
    :
    await stripe.confirmCardPayment(clientSecret, options);

  if (response.error) {
    const debugValues = {
      clientSecret,
      paymentMethodId,
      nextUrl
    };
    throw new PaymentError(
      genericErrorMessage,
      `Error in confirmStripePayment with values ${JSON.stringify(debugValues)} and response ${JSON.stringify(response)}`
    );
  } else {
    return { nextUrl };
  }
};

export const createStripePaymentMethod = async (stripe, card, values) => {
  const { cardName, zipCode } = values;
  const billingDetails = {
    name: cardName,
    // The registration form nests the user's attributes; other payment forms have no email.
    email: values.user?.email || null,
    address: {
      postal_code: zipCode
    }
  };

  // Alipay is taxed by the billing ZIP collected on checkout, like a card
  const payload = card !== null ?
    { type: 'card', card, billing_details: billingDetails } :
    { type: 'alipay', billing_details: billingDetails };

  const response = await stripe.createPaymentMethod(payload);

  if (response.error) {
    throw new PaymentError(
      parseStripeError(response.error),
      `Error in createStripePaymentMethod with values ${JSON.stringify(values)} and response ${JSON.stringify(response)}`
    );
  } else {
    return { stripePaymentMethod: response.paymentMethod };
  }
};

// `applePay` describes the charges to come when the wallet will be billed again, which is what
// gets Apple to hand over a merchant token that keeps working when the student changes devices.
export const createStripePaymentRequest = async (stripe, name, priceCents, applePay = undefined) => {
  const response = await stripe.paymentRequest({
    country: 'US',
    currency: 'usd',
    total: {
      label: name,
      amount: priceCents
    },
    requestPayerName: true,
    requestPayerEmail: false,
    ...(applePay ? { applePay } : {})
  });

  if (response.error) {
    const debugValues = {
      applePay,
      name,
      priceCents
    };
    throw new PaymentError(
      null,
      `Error in createStripePaymentRequest with values ${JSON.stringify(debugValues)} and response ${JSON.stringify(response)}`
    );
  } else {
    return response;
  }
};

export const createStripeToken = async (stripe, card): Promise<{ purchaseToken: string; }> => {
  const response = await stripe.createToken(card);

  if (response.error) {
    throw new PaymentError(
      parseStripeError(response.error),
      `Error in createStripeToken with response ${JSON.stringify(response)}`
    );
  } else {
    return { purchaseToken: response.token.id };
  }
};

export const getPurchaseAgreementPricing = (subscriptionPlanId, couponCode, paymentMethod, zipCode) => {
  const path = paths.api.purchaseAgreementPricing(
    subscriptionPlanId,
    couponCode,
    paymentMethod,
    zipCode
  );

  return fetch(path).then((response) => response.pricing);
};

export const queueCartAbandonFollowup = (email, subscriptionPlanId, authenticityToken) => {
  return fetch(paths.api.queueCartAbandonFollowup(), {
    method: 'POST',
    body: JSON.stringify({
      authenticityToken,
      email,
      subscriptionPlanId
    })
  });
};

const parseStripeError = (error) => {
  if (error.code === 'email_invalid') {
    return { email: error.message };
  } else if (error.type === 'card_error' || error.type === 'validation_error') {
    if (error.message.indexOf('expiration date') !== -1 || error.message.indexOf('expiration year') !== -1) {
      return { stripeExpirationDate: error.message };
    } else if (error.message.indexOf('card number') !== -1) {
      return { stripeCardNumber: error.message };
    } else if (error.message.indexOf('security code') !== -1) {
      return { stripeSecurityCode: error.message };
    } else {
      return { _error: error.message };
    }
  } else {
    return { _error: error.message };
  }
};
